import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Check, Store } from 'lucide-react';
import { PLANS, CATEGORIES, DISTRICTS } from '../data/mockData';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { PlanBadge } from '../components/commerce/PlanBadge';
import { toast } from 'sonner';

const STEPS = ['Business', 'Plan', 'Payment', 'Review'];

export default function MerchantRegisterPage() {
  const { registerMerchantLocal } = useApp();
  const { user, becomeMerchant } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ businessName: '', merchantType: 'FNB', categories: ['Food & Beverage'], district: DISTRICTS[0], address: '', mapsUrl: '', whatsapp: '', plan: 'STORE', billing: 'monthly', bankHolder: '', bankName: '', bankAccount: '', operatingHours: { open: '09:00', close: '18:00', closedDays: [] } });

  const MERCHANT_TYPES = [
    { id: 'FNB', label: 'Food & Beverage', desc: 'Prepared food/drinks. Customers can only order while you are open.' },
    { id: 'PRODUCT', label: 'Retail / Products', desc: 'Physical goods. After hours, orders become next-day delivery.' },
    { id: 'SERVICE', label: 'Services', desc: 'Appointments & services. Customers book via WhatsApp appointment.' },
  ];
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    if (user === false) navigate('/account/login?redirect=/merchant/register', { replace: true });
  }, [user, navigate]);

  if (!user) return null;

  if (user.role === 'merchant') {
    return (
      <div className="mx-auto max-w-[520px] px-4 py-20 text-center" data-testid="already-merchant">
        <div className="rounded-3xl bg-kelate-surface p-10 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
          <Store size={40} className="mx-auto text-kelate-wood" strokeWidth={1.5} />
          <h1 className="mt-4 font-display text-2xl font-semibold text-kelate-darkwood">You already have a store</h1>
          <p className="mt-2 text-sm text-kelate-soft">Manage your products, orders and plan in the Merchant Center.</p>
          <Link to="/merchant-center" data-testid="go-merchant-center" className="mt-6 inline-block rounded-xl bg-kelate-darkwood px-6 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Go to Merchant Center</Link>
        </div>
      </div>
    );
  }

  const next = async () => {
    if (step === 0 && (!form.businessName || !form.address || form.categories.length === 0)) return toast.error('Fill in business details and choose at least one category');
    if (step === 0 && form.merchantType === 'FNB' && (!form.operatingHours.open || !form.operatingHours.close)) return toast.error('F&B businesses must set operating hours so customers know when they can order');
    if (step === 3) {
      setSubmitting(true);
      const res = await becomeMerchant();
      setSubmitting(false);
      if (!res.ok) { toast.error(res.error); return; }
      const reg = await registerMerchantLocal(res.user.merchantId, { ...form, email: user.email, phone: user.phone || form.whatsapp });
      if (reg?.error) return;
      toast.success('Application submitted', { description: 'KELATE will verify your business and activate your plan.' });
      navigate('/merchant/register/success');
      return;
    }
    setStep(step + 1);
  };

  const plan = PLANS[form.plan];
  const price = form.billing === 'monthly' ? plan.monthly : plan.annual;
  const input = "h-10 w-full rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";

  return (
    <div data-testid="merchant-register-page" className="mx-auto max-w-[860px] px-4 py-10">
      <div className="text-center">
        <h1 className="font-display text-3xl font-semibold text-kelate-darkwood sm:text-4xl">Become a KELATE Merchant</h1>
        <p className="mt-1 text-sm text-kelate-soft">List your business, sell online, let KELATE handle delivery and payment collection.</p>
        <p className="mt-2 text-xs text-kelate-soft" data-testid="register-account-note">Registering as <b className="text-kelate-ink">{user.name}</b> ({user.email})</p>
      </div>
      <div className="mt-8 flex items-center justify-center gap-0" data-testid="register-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center">
              <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i < step ? 'bg-kelate-sage text-white' : i === step ? 'bg-kelate-darkwood text-kelate-surface' : 'bg-secondary text-kelate-soft'}`}>
                {i < step ? <Check size={13} /> : i + 1}
              </span>
              <span className={`mt-1 text-[10px] ${i === step ? 'font-semibold text-kelate-ink' : 'text-kelate-soft'}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <span className={`mx-1 mb-4 h-px w-8 sm:w-14 ${i < step ? 'bg-kelate-sage' : 'bg-kelate-line'}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-8 rounded-md border border-kelate-line bg-kelate-surface p-6">
        {step === 0 && (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="step-business">
            <input data-testid="mr-business-name" placeholder="Business name" value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} className={input} />
            <div className="sm:col-span-2" data-testid="mr-merchant-type">
              <label className="mb-1.5 block text-xs font-medium text-kelate-ink">What does your business offer?</label>
              <div className="grid gap-2 sm:grid-cols-3">
                {MERCHANT_TYPES.map((t) => (
                  <button key={t.id} type="button" data-testid={`mr-type-${t.id.toLowerCase()}`} onClick={() => setForm({ ...form, merchantType: t.id })}
                    className={`rounded-xl border p-3 text-left transition-colors ${form.merchantType === t.id ? 'border-kelate-darkwood bg-secondary/50' : 'border-kelate-line bg-kelate-bg hover:border-kelate-wood'}`}>
                    <div className="text-sm font-semibold text-kelate-ink">{t.label}</div>
                    <div className="mt-0.5 text-[11px] text-kelate-soft">{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2" data-testid="mr-operating-hours">
              <label className="mb-1.5 block text-xs font-medium text-kelate-ink">Operating hours {form.merchantType === 'FNB' && <span className="text-kelate-red">(required for F&B)</span>}</label>
              <div className="flex items-center gap-2">
                <input data-testid="mr-hours-open" type="time" value={form.operatingHours.open} onChange={(e) => setForm({ ...form, operatingHours: { ...form.operatingHours, open: e.target.value } })} className={input} />
                <span className="text-xs text-kelate-soft">to</span>
                <input data-testid="mr-hours-close" type="time" value={form.operatingHours.close} onChange={(e) => setForm({ ...form, operatingHours: { ...form.operatingHours, close: e.target.value } })} className={input} />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {DAYS.map((d, i) => (
                  <button key={d} type="button" data-testid={`mr-closed-day-${d.toLowerCase()}`}
                    onClick={() => {
                      const cd = form.operatingHours.closedDays || [];
                      setForm({ ...form, operatingHours: { ...form.operatingHours, closedDays: cd.includes(i) ? cd.filter((x) => x !== i) : [...cd, i] } });
                    }}
                    className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${(form.operatingHours.closedDays || []).includes(i) ? 'border-kelate-red bg-kelate-red/10 text-kelate-red' : 'border-kelate-line bg-kelate-bg text-kelate-soft hover:border-kelate-wood'}`}>
                    {d} off
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-kelate-soft">
                {form.merchantType === 'FNB' ? 'Customers can only order during these hours.' : form.merchantType === 'PRODUCT' ? 'Orders placed after closing are delivered the next day.' : 'Customers will see when you are available for appointments.'}
              </p>
            </div>
            <div className="sm:col-span-2" data-testid="mr-categories">
              <label className="mb-1.5 block text-xs font-medium text-kelate-ink">Business categories (choose all that apply)</label>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`mr-cat-${c.toLowerCase().replace(/[\s&]+/g, '-')}`}
                    onClick={() => setForm({ ...form, categories: form.categories.includes(c) ? form.categories.filter((x) => x !== c) : [...form.categories, c] })}
                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${form.categories.includes(c) ? 'border-kelate-darkwood bg-kelate-darkwood text-kelate-surface' : 'border-kelate-line bg-kelate-bg text-kelate-soft hover:border-kelate-wood'}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <select data-testid="mr-district" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className={input}>{DISTRICTS.map((d) => <option key={d}>{d}</option>)}</select>
            <input data-testid="mr-whatsapp" placeholder="WhatsApp number" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className={input} />
            <input data-testid="mr-address" placeholder="Business address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={`${input} sm:col-span-2`} />
            <input data-testid="mr-maps-url" placeholder="Google Maps link (for Visit Store button) — Share > Copy link from Google Maps" value={form.mapsUrl} onChange={(e) => setForm({ ...form, mapsUrl: e.target.value })} className={`${input} sm:col-span-2`} />
            <div className="grid gap-3 sm:col-span-2 sm:grid-cols-3">
              <input data-testid="mr-bank-holder" placeholder="Bank account holder" value={form.bankHolder} onChange={(e) => setForm({ ...form, bankHolder: e.target.value })} className={input} />
              <input data-testid="mr-bank-name" placeholder="Bank name" value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} className={input} />
              <input data-testid="mr-bank-account" placeholder="Account number (for settlement)" value={form.bankAccount} onChange={(e) => setForm({ ...form, bankAccount: e.target.value })} className={input} />
            </div>
            <p className="text-[11px] text-kelate-soft sm:col-span-2">Bank details are stored securely for KELATE settlement payouts and are never shown publicly.</p>
          </div>
        )}
        {step === 1 && (
          <div data-testid="step-plan">
            <div className="mb-4 flex justify-center gap-2">
              {['monthly', 'annual'].map((b) => (
                <button key={b} data-testid={`billing-${b}`} onClick={() => setForm({ ...form, billing: b })} className={`rounded-sm px-4 py-1.5 text-sm ${form.billing === b ? 'bg-kelate-darkwood text-kelate-surface' : 'border border-kelate-line text-kelate-soft'}`}>
                  {b === 'monthly' ? 'Monthly' : 'Annual (pay 10, get 12)'}
                </button>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Object.values(PLANS).map((p) => (
                <button key={p.name} data-testid={`plan-${p.name.toLowerCase()}`} onClick={() => setForm({ ...form, plan: p.name })}
                  className={`rounded-md border p-4 text-left transition-colors ${form.plan === p.name ? 'border-kelate-darkwood bg-secondary/50' : 'border-kelate-line bg-kelate-surface hover:border-kelate-wood'}`}>
                  <PlanBadge plan={p.name} />
                  <div className="mt-2 text-xl font-semibold text-kelate-darkwood">RM{form.billing === 'monthly' ? p.monthly : p.annual}<span className="text-xs font-normal text-kelate-soft">/{form.billing === 'monthly' ? 'mo' : 'yr'}</span></div>
                  <div className="text-xs text-kelate-soft">{p.goal}</div>
                  <ul className="mt-2 space-y-1 text-[11px] text-kelate-soft">
                    {p.features.slice(0, 4).map((f) => <li key={f} className="flex gap-1"><Check size={11} className="mt-0.5 shrink-0 text-kelate-sage" />{f}</li>)}
                  </ul>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="mx-auto max-w-sm" data-testid="step-payment">
            <div className="rounded-md border border-kelate-line bg-secondary/40 p-5 text-center">
              <div className="text-xs uppercase tracking-widest text-kelate-soft">Subscription payment</div>
              <div className="mt-2 text-3xl font-semibold text-kelate-darkwood" data-testid="subscription-amount">RM{price}</div>
              <div className="text-xs text-kelate-soft">{plan.name} plan · billed {form.billing}</div>
              <p className="mt-4 rounded-sm bg-kelate-surface px-3 py-2.5 text-[11px] leading-relaxed text-kelate-soft">
                No online payment is taken here. KELATE will contact you to verify your subscription payment before activating your plan features.
              </p>
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="mx-auto max-w-md" data-testid="step-review">
            <dl className="space-y-2 text-sm">
              {[['Name', user.name], ['Email', user.email], ['Business', form.businessName], ['Categories', form.categories.join(', ')], ['District', form.district], ['Plan', `${plan.name} · RM${price}/${form.billing === 'monthly' ? 'mo' : 'yr'}`], ['Bank', form.bankName ? `${form.bankName} ******${form.bankAccount.slice(-4)}` : '-']].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-kelate-line/60 pb-2"><dt className="text-kelate-soft">{k}</dt><dd className="font-medium text-kelate-ink">{v || '-'}</dd></div>
              ))}
            </dl>
          </div>
        )}
        <div className="mt-6 flex justify-between">
          <button data-testid="step-back" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="rounded-sm border border-kelate-line px-5 py-2.5 text-sm text-kelate-soft disabled:opacity-40">Back</button>
          <button data-testid={step === 3 ? 'mr-submit' : 'step-next'} onClick={next} disabled={submitting} className="rounded-sm bg-kelate-darkwood px-6 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood disabled:opacity-50">
            {step === 3 ? (submitting ? 'Submitting...' : 'Submit Application') : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MerchantRegisterSuccess() {
  return (
    <div className="mx-auto max-w-[520px] px-4 py-20 text-center" data-testid="merchant-register-success">
      <div className="rounded-md border border-kelate-line bg-kelate-surface p-10">
        <Store size={40} className="mx-auto text-kelate-wood" strokeWidth={1.5} />
        <h1 className="mt-4 font-display text-2xl font-semibold text-kelate-darkwood">Application Received</h1>
        <p className="mt-2 text-sm text-kelate-soft">KELATE will verify your business details and activate your plan. You will be notified once approved.</p>
        <div className="mt-6 flex justify-center gap-2">
          <Link to="/merchant-center" data-testid="go-merchant-center" className="inline-block rounded-sm bg-kelate-darkwood px-6 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Go to Merchant Center</Link>
          <Link to="/" data-testid="back-home" className="inline-block rounded-sm border border-kelate-darkwood px-6 py-2.5 text-sm font-medium text-kelate-darkwood hover:bg-secondary">Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
